import * as Sentry from '@sentry/nextjs';
import { atom } from 'jotai';
import type { Getter, Setter } from 'jotai';

import {
  audioContextAtom,
  audioElementAtom,
  audioPlayerErrorAtom,
  awaitingPlaybackAtom,
  gainNodeAtom,
  mutePreferenceAtom,
  playbackAtom,
  volumePreferenceAtom,
} from '@/features/AudioPlayer/atoms/core/primaryAtoms';
import {
  getBoundAudioElement,
  getMediaElementSource,
  setMediaElementSource,
} from '@/features/AudioPlayer/atoms/playback/webAudioGraph';
// eslint-disable-next-line import/no-cycle
import {
  currentTrackAtom,
  currentTrackIdAtom,
  trackDurationAtomFamily,
  trackSeekCompletionAtomFamily,
} from '@/features/AudioPlayer/atoms/player/trackAtoms';
import {
  activeQueueHasMorePagesAtom,
  currentPagePaginationAtom,
  waitingForNextPageAtom,
} from '@/features/AudioPlayer/atoms/queue/derivedQueueAtoms';
import { queueAtom } from '@/features/AudioPlayer/atoms/queue/queueAtoms';
import { logEvent } from '@/pages/api/v1/utils';
import { formatTime, log } from '@/lib/utils';
import {
  getBrowserLabel,
  needsDelayedAudioCreation,
} from '@/utils/browserDetection';

/**
 * PLAYBACK CONTROL ATOMS
 * These atoms handle playback operations like play/pause, seeking, and navigation
 */

/**
 * Get whether audio is currently playing
 */
export const isPlayingAtom = atom<boolean>(
  (get) => get(playbackAtom).isPlaying
);
isPlayingAtom.debugLabel = 'isPlayingAtom';

/**
 * Get whether there is a loaded next track in the queue
 */
export const hasLoadedNextTrackAtom = atom<boolean>((get) => {
  const queue = get(queueAtom);

  return queue.currentIndex < queue.trackOrder.length - 1;
});
hasLoadedNextTrackAtom.debugLabel = 'hasLoadedNextTrackAtom';

/**
 * Get whether there is a next track to play. True when a track is already
 * loaded after the current one, or — for a browse/search queue still playing
 * from its originating page — when the server has further pages to fetch
 * (DEV-6133). The latter keeps autoplay going past the loaded page boundary:
 * `skipToNextTrackAtom` fetches the next page and resumes once it arrives.
 */
export const hasNextTrackAtom = atom<boolean>((get) => {
  if (get(hasLoadedNextTrackAtom)) {
    return true;
  }

  return get(activeQueueHasMorePagesAtom);
});
hasNextTrackAtom.debugLabel = 'hasNextTrackAtom';

/**
 * Get whether there is a previous track in the queue
 */
export const hasPreviousTrackAtom = atom<boolean>((get) => {
  const queue = get(queueAtom);

  return queue.currentIndex > 0;
});
hasPreviousTrackAtom.debugLabel = 'hasPreviousTrackAtom';

/**
 * Get the current playback time in seconds
 */
export const currentTimeAtom = atom<number>(
  (get) => get(playbackAtom).currentTime
);
currentTimeAtom.debugLabel = 'currentTimeAtom';

/**
 * Get the total duration of the current track in seconds
 */
export const durationAtom = atom<number>((get) => get(playbackAtom).duration);
durationAtom.debugLabel = 'durationAtom';

/**
 * Get whether there is an error in playback
 */
export const hasErrorAtom = atom<boolean>(
  (get) => get(audioPlayerErrorAtom) !== null
);
hasErrorAtom.debugLabel = 'hasErrorAtom';

/**
 * Clear any playback errors
 */
export const clearErrorAtom = atom(null, (get, set) => {
  set(audioPlayerErrorAtom, null);
});
clearErrorAtom.debugLabel = 'clearErrorAtom';

/**
 * Get the playback progress as a percentage (0-1)
 * Used for progress bars and seeking
 */
export const progressPercentAtom = atom<number>((get) => {
  const { currentTime, duration } = get(playbackAtom);

  if (duration === 0) {
    return 0;
  }

  return Math.min(currentTime / duration, 1);
});
progressPercentAtom.debugLabel = 'progressPercentAtom';

/**
 * Optimized formatted current time atom
 * Only updates when the formatted time string actually changes, preventing unnecessary re-renders
 * of CurrentTimeDisplay component (which was re-rendering 60fps even when seconds didn't change)
 */
export const formattedCurrentTimeAtom = atom<string>((get) => {
  const currentTime = get(currentTimeAtom);
  return formatTime(currentTime);
});
formattedCurrentTimeAtom.debugLabel = 'formattedCurrentTimeAtom';

/**
 * Optimized formatted duration atom
 * Only updates when the formatted duration string actually changes
 */
export const formattedDurationAtom = atom<string>((get) => {
  const duration = get(durationAtom);
  return formatTime(duration > 0 && duration < 1 ? 1 : duration);
});
formattedDurationAtom.debugLabel = 'formattedDurationAtom';

/**
 * Atom to handle seeking to a position in a track
 * Works with percentage values (0-1) for easier UI integration
 */
export const seekPositionAtom = atom(
  null,
  (get, set, payload: { trackId: number; percentage: number }) => {
    const { trackId, percentage } = payload;
    const audioElement = get(audioElementAtom);
    const currentTrackId = get(currentTrackIdAtom);
    const playback = get(playbackAtom);

    // Get track-specific duration to ensure correct position calculation
    const trackDurationAtom = trackDurationAtomFamily(trackId);
    const trackDuration = get(trackDurationAtom);

    // Only seek if this is the current track and audio is available
    if (audioElement && currentTrackId === trackId && trackDuration > 0) {
      // Clamp percentage to valid range (0-1)
      const clampedPercent = Math.max(0, Math.min(percentage, 1));
      const newTime = clampedPercent * trackDuration;

      audioElement.currentTime = newTime;

      // Update playback state immediately for responsive UI
      set(playbackAtom, {
        ...playback,
        currentTime: newTime,
      });

      log('Seeked using track-specific duration:', {
        trackId,
        percentage,
        trackDuration,
        newTime,
      });

      // Update track-specific seek completion atom to notify this waveform
      const trackSeekCompletionAtom = trackSeekCompletionAtomFamily(trackId);
      set(trackSeekCompletionAtom, Date.now());
    }
  }
);
seekPositionAtom.debugLabel = 'seekPositionAtom';

/**
 * Skip to the next track
 */
export const skipToNextTrackAtom = atom(null, (get, set) => {
  const currentQueue = get(queueAtom);
  const { currentIndex } = currentQueue;
  const nextIndex = currentIndex + 1;

  if (nextIndex < currentQueue.trackOrder.length) {
    set(queueAtom, {
      ...currentQueue,
      currentIndex: nextIndex,
    });
    return;
  }

  // Ran off the end of the loaded tracks. If the originating page has more
  // pages server-side, fetch the next one and flag that we're waiting — the
  // auto-pagination hook appends it and advances once it arrives (DEV-6133).
  if (get(activeQueueHasMorePagesAtom)) {
    set(waitingForNextPageAtom, true);
    void get(currentPagePaginationAtom)?.fetchNextPage();
  }
});
skipToNextTrackAtom.debugLabel = 'skipToNextTrackAtom';

/**
 * Skip to the previous track
 */
export const skipToPreviousTrackAtom = atom(null, (get, set) => {
  const currentQueue = get(queueAtom);
  const { currentIndex } = currentQueue;
  const previousIndex = currentIndex - 1;

  if (previousIndex >= 0) {
    set(queueAtom, {
      ...currentQueue,
      currentIndex: previousIndex,
    });
  }
});
skipToPreviousTrackAtom.debugLabel = 'skipToPreviousTrackAtom';

/**
 * Seek by percent atom - alternative to seekPositionAtom with clearer naming
 */
export const seekByPercentAtom = atom(
  null,
  (get, set, payload: { trackId: number; percentage: number }) => {
    const { trackId, percentage } = payload;
    const audioElement = get(audioElementAtom);
    const currentTrackId = get(currentTrackIdAtom);
    const playback = get(playbackAtom);
    const { duration } = playback;

    if (audioElement && currentTrackId === trackId) {
      const newTime = percentage * duration;

      audioElement.currentTime = newTime;

      set(playbackAtom, {
        ...playback,
        currentTime: newTime,
      });
    }
  }
);
seekByPercentAtom.debugLabel = 'seekByPercentAtom';

/**
 * Safari-specific audio element creation during user interaction
 *
 * Safari (both iOS and desktop) has strict autoplay policies that require:
 * 1. Audio elements MUST be created during a user interaction (tap/click)
 * 2. The audio.play() call MUST occur within the same call stack as the user interaction
 * 3. Any async operations between user interaction and play() will break the gesture context
 *
 * Additionally, Safari will block audio playback if:
 * - The Audio element was created outside of a user gesture
 * - The audio source is set asynchronously after the user gesture
 * - Multiple audio elements are created (Safari limits concurrent audio)
 *
 * This function ensures Safari compliance by:
 * - Creating the Audio element only during the first user interaction (play button click)
 * - Setting the audio source immediately when creating the element
 * - Maintaining a global reference to prevent multiple audio elements
 * - Preventing the DOM audio element from overwriting our programmatic one
 *
 * Affected browsers:
 * - All iOS devices (iPhone, iPad, iPod)
 * - Desktop Safari 17.x on macOS (especially on older macOS 10 and newer versions)
 */
// Store a global reference to ensure we're using the same audio element on Safari
let globalAudioElement: HTMLAudioElement | null = null;

// Module-level Web Audio singletons. AudioContext + GainNode live for the page
// lifetime; MediaElementAudioSourceNode is per-element and recreated whenever
// the element is replaced (or torn down on resetAudioPlayerAtom).
let audioContext: AudioContext | null = null;
let gainNode: GainNode | null = null;
let lastContextState: AudioContextState | null = null;
// The statechange listener is registered once (the AudioContext is a long-lived
// singleton). It needs the *current* Jotai get/set, not the ones from its
// first invocation — so we keep them in module-level refs that every
// createAudioGraph call refreshes.
let currentGet: Getter | null = null;
let currentSet: Setter | null = null;

/**
 * Test-only helper: drops references to the module-level Web Audio singletons
 * so the next createAudioGraph call constructs a fresh context. Production
 * code must never call this.
 */
export const __resetWebAudioGraphForTesting = () => {
  audioContext = null;
  gainNode = null;
  lastContextState = null;
  currentGet = null;
  currentSet = null;
};

/**
 * Creates the Web Audio graph: AudioContext -> GainNode -> destination.
 * Idempotent — context + gain are reused across resets; source node is
 * recreated whenever the audio element is replaced.
 *
 * Must only be called from a client-side context. Safe to call multiple times.
 */
export const createAudioGraph = (
  get: Getter,
  set: Setter,
  audioElement: HTMLAudioElement
) => {
  if (typeof window === 'undefined') {
    return;
  }

  const browserLabel = getBrowserLabel();

  // Refresh the get/set refs every call so the long-lived statechange listener
  // always writes to the current store.
  currentGet = get;
  currentSet = set;

  if (!audioContext) {
    // No try/catch — let creation failures surface to Sentry uncaught.
    audioContext = new AudioContext();
    gainNode = audioContext.createGain();
    gainNode.connect(audioContext.destination);
    lastContextState = audioContext.state;

    audioContext.addEventListener('statechange', () => {
      if (!(audioContext && currentGet && currentSet)) {
        return;
      }
      const isPlaying = currentGet(playbackAtom).isPlaying;
      const fromState = lastContextState;
      const newState = audioContext.state;
      lastContextState = newState;

      Sentry.addBreadcrumb({
        category: 'web_audio',
        message: `AudioContext state: ${fromState ?? '?'} -> ${newState}`,
        level: 'info',
        data: {
          from: fromState,
          to: newState,
          isPlaying,
          browser: browserLabel,
        },
      });

      void logEvent({
        event_type: 'audio_context_state_change',
        asPath: window.location.pathname,
        metadata: {
          from: fromState ?? undefined,
          to: newState,
          is_playing: isPlaying,
          browser: browserLabel,
        },
      });

      if (newState === 'suspended' && isPlaying) {
        void audioContext.resume().catch((error: Error) => {
          Sentry.captureException(error, {
            tags: { component: 'AudioPlayer', action: 'audio_context_resume' },
          });
          void logEvent({
            event_type: 'audio_context_resume_failed',
            asPath: window.location.pathname,
            metadata: { browser: browserLabel, error: error.message },
          });
          currentSet?.(audioPlayerErrorAtom, {
            code: 'PLAYBACK_ERROR',
            message: 'AudioContext failed to resume',
            timestamp: Date.now(),
            details: error,
            recoverable: true,
          });
        });
      } else if (newState === 'closed') {
        currentSet(audioPlayerErrorAtom, {
          code: 'PLAYBACK_ERROR',
          message: 'AudioContext closed unexpectedly',
          timestamp: Date.now(),
          recoverable: true,
        });
      }
    });

    void logEvent({
      event_type: 'web_audio_graph_created',
      asPath: window.location.pathname,
      metadata: {
        context_state_initial: audioContext.state,
        browser: browserLabel,
        flag: true,
      },
    });

    if (process.env.NODE_ENV !== 'production') {
      (window as unknown as { __audioCtx?: AudioContext }).__audioCtx =
        audioContext;
    }

    Sentry.addBreadcrumb({
      category: 'web_audio',
      message: 'AudioContext + GainNode created',
      level: 'info',
      data: { browser: browserLabel, state: audioContext.state },
    });
  }

  // Always sync the singletons into the current Jotai store. The store may be
  // recreated (e.g. in tests) while the module-level singletons live on.
  set(audioContextAtom, audioContext);
  set(gainNodeAtom, gainNode);

  // Apply current volume/mute preferences to the gain node. The React-side
  // volume effect also handles this on subsequent changes; seeding here
  // ensures the graph starts at the user's chosen level rather than 1.
  if (gainNode) {
    const initialVolume = get(volumePreferenceAtom);
    const initialMuted = get(mutePreferenceAtom);
    gainNode.gain.value = initialMuted ? 0 : initialVolume;
  }

  // Ensure crossOrigin is set on the element that will actually receive
  // .src= — without this, MediaElementAudioSourceNode silently outputs
  // silence when the response isn't CORS-clean.
  if (audioElement.crossOrigin !== 'anonymous') {
    audioElement.crossOrigin = 'anonymous';
  }

  // Rewire the source node when the audio element is swapped (e.g. non-Safari
  // DOM ref overwrites the programmatic Audio()). MediaElementAudioSourceNode
  // is permanently bound to its source element, so we must disconnect the old
  // one and create a new one for the current element.
  const existingSource = getMediaElementSource();
  const boundElement = getBoundAudioElement();
  if (existingSource && boundElement !== audioElement) {
    existingSource.disconnect();
    setMediaElementSource(null);
  }

  if (!getMediaElementSource() && audioContext && gainNode) {
    // createMediaElementSource throws if the same element is wrapped twice —
    // module singleton guarantees one-per-element.
    const source = audioContext.createMediaElementSource(audioElement);
    source.connect(gainNode);
    setMediaElementSource(source, audioElement);

    // Only resume when actually suspended; calling on a running context is a
    // no-op but still schedules a microtask + statechange event in some browsers.
    if (audioContext.state === 'suspended') {
      void audioContext.resume().catch((error: Error) => {
        Sentry.captureException(error, {
          tags: { component: 'AudioPlayer', action: 'audio_context_resume' },
        });
        void logEvent({
          event_type: 'audio_context_resume_failed',
          asPath: window.location.pathname,
          metadata: { browser: browserLabel, error: error.message },
        });
      });
    }

    Sentry.addBreadcrumb({
      category: 'web_audio',
      message: 'MediaElementSource connected',
      level: 'info',
      data: { browser: browserLabel },
    });
  }
};

const createAudioElementForSafari = (get: Getter, set: Setter) => {
  const requiresDelayedCreation = needsDelayedAudioCreation();
  const existingAudioElement = get(audioElementAtom);

  // Only create if Safari and no audio element exists yet
  if (requiresDelayedCreation && !existingAudioElement && !globalAudioElement) {
    const browserLabel = getBrowserLabel();
    log(`[${browserLabel}] Creating audio element during user interaction`);
    const newAudioElement = new Audio();
    // crossOrigin must be set BEFORE .src= so the request is CORS-clean.
    // Required for MediaElementAudioSourceNode — without it the node outputs
    // silence with no JS error.
    newAudioElement.crossOrigin = 'anonymous';
    newAudioElement.preload = 'auto';

    // Store globally to ensure we use the same instance
    globalAudioElement = newAudioElement;

    // Get the current track and set its source immediately
    const currentTrack = get(currentTrackAtom);
    if (currentTrack?.version_preview_uri) {
      newAudioElement.src = currentTrack.version_preview_uri;
      newAudioElement.load();
      log(
        `[${browserLabel}] Audio element created with source: ${currentTrack.version_preview_uri}`
      );
    }

    set(audioElementAtom, newAudioElement);

    createAudioGraph(get, set, newAudioElement);

    // Comprehensive Safari audio creation logging
    Sentry.addBreadcrumb({
      category: 'safari_audio',
      message: `${browserLabel} audio element created successfully`,
      level: 'info',
      data: {
        action: 'create_audio_element',
        browser: browserLabel,
        hasTrackUrl: !!currentTrack?.version_preview_uri,
        trackUrl: currentTrack?.version_preview_uri?.slice(0, 100),
        trackName: currentTrack?.name,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      },
    });
  } else if (
    requiresDelayedCreation &&
    globalAudioElement &&
    !existingAudioElement
  ) {
    // Restore the global audio element to the atom if it's missing
    // This can happen if other code tries to clear the atom
    const browserLabel = getBrowserLabel();
    log(`[${browserLabel}] Restoring global audio element to atom`);
    set(audioElementAtom, globalAudioElement);

    // Log restoration events - these might indicate issues
    Sentry.captureMessage(
      `${browserLabel} audio element restored from global reference`,
      {
        level: 'warning',
        tags: {
          component: 'AudioPlayer',
          platform: browserLabel,
          action: 'restore_audio_element',
        },
        extra: {
          reason: 'Audio atom was cleared but global reference exists',
          userAgent: navigator.userAgent,
        },
      }
    );
  } else if (requiresDelayedCreation && existingAudioElement) {
    // Log when we're reusing existing element
    const browserLabel = getBrowserLabel();
    Sentry.addBreadcrumb({
      category: 'safari_audio',
      message: `${browserLabel} using existing audio element`,
      level: 'debug',
      data: {
        action: 'reuse_audio_element',
        browser: browserLabel,
        hasGlobalElement: !!globalAudioElement,
        timestamp: new Date().toISOString(),
      },
    });
  }
};

/**
 * Atom to control playback (play/pause/toggle)
 * Read returns the current playing state
 * Write accepts actions to control playback
 */
export const playbackControlAtom = atom(
  (get) => get(playbackAtom).isPlaying,
  (get, set, action: 'play' | 'pause' | 'toggle') => {
    log('playbackControlAtom called with action:', action);

    Sentry.addBreadcrumb({
      category: 'audio_player',
      message: `Playback control: ${action}`,
      level: 'info',
    });

    // For Safari browsers, create audio element during user interaction
    // This MUST happen synchronously during the user gesture
    createAudioElementForSafari(get, set);

    const audioElement = get(audioElementAtom);
    const playback = get(playbackAtom);
    const currentTrack = get(currentTrackAtom);
    const awaitingPlayback = get(awaitingPlaybackAtom);

    if (!audioElement) {
      // For pause actions, cancel any pending autoplay before returning
      if (action === 'pause') {
        if (awaitingPlayback) {
          log(
            'No audio element available, clearing awaitingPlayback for pause action'
          );
          set(awaitingPlaybackAtom, false);
        } else {
          log('No audio element available, ignoring pause action');
        }
        return;
      }

      if (!awaitingPlayback) {
        log(
          'No audio element available for playback, setting awaitingPlayback'
        );
        set(awaitingPlaybackAtom, true);
      }

      return;
    }

    const trackInfo = currentTrack
      ? { id: currentTrack.id, name: currentTrack.name }
      : 'No track selected';

    Sentry.addBreadcrumb({
      category: 'audio_player',
      message: `Current track for ${action}: ${JSON.stringify(trackInfo)}`,
      level: 'info',
    });

    let newIsPlaying = playback.isPlaying;

    const handlePlaybackError = (
      error: Error,
      data?: Record<string, unknown>
    ) => {
      // Don't treat AbortError as an exception, it's just a user changing tracks
      if (error.name === 'AbortError') {
        log('Play aborted - user likely changed tracks');
        Sentry.addBreadcrumb({
          category: 'audio_player',
          message: 'Play aborted - user changed tracks',
          level: 'info',
          data: { currentTrack: trackInfo },
        });
      } else if (error.name === 'NotAllowedError') {
        // Mobile autoplay restriction
        log('Playback not allowed - likely mobile autoplay restriction');
        Sentry.addBreadcrumb({
          category: 'audio_player',
          message: 'Playback not allowed - mobile autoplay restriction',
          level: 'warning',
          data: {
            currentTrack: trackInfo,
            userAgent: navigator.userAgent,
            ...data,
          },
        });

        // For mobile autoplay restrictions, don't set awaiting playback as it can cause infinite loading
        // Just set playing to false and let user try again
        newIsPlaying = false;
      } else {
        console.error('Error playing audio:', error);
        // Log real errors as exceptions
        Sentry.captureException(error, {
          tags: { component: 'AudioPlayer', action: 'play' },
          extra: { currentTrack: trackInfo },
        });

        newIsPlaying = false;
      }
    };

    // For mobile devices, ensure we're in a proper user interaction context
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    switch (action) {
      case 'play':
        // On mobile, always attempt to play synchronously to preserve user gesture
        if (isMobile) {
          // Log Safari play attempts for monitoring
          const requiresDelayedCreation = needsDelayedAudioCreation();
          if (requiresDelayedCreation) {
            const browserLabel = getBrowserLabel();
            Sentry.addBreadcrumb({
              category: 'safari_audio',
              message: `${browserLabel} audio play initiated`,
              level: 'info',
              data: {
                action: 'play_started',
                browser: browserLabel,
                hasAudioSrc: !!audioElement.src,
                audioReadyState: audioElement.readyState,
                timestamp: new Date().toISOString(),
              },
            });
          }

          try {
            const playPromise = audioElement.play();
            newIsPlaying = true;
            // Handle the promise without breaking the synchronous flow
            playPromise.catch((error) => {
              // Enhanced Safari error tracking
              const requiresDelayedCreation = needsDelayedAudioCreation();

              // For AbortError, it often means the audio is still loading
              // The play will resume automatically once loaded
              if (error.name === 'AbortError') {
                const browserLabel = getBrowserLabel();
                log(
                  `[${browserLabel}] Play aborted - audio still loading, will retry automatically`
                );

                if (requiresDelayedCreation) {
                  Sentry.addBreadcrumb({
                    category: 'safari_audio',
                    message: `${browserLabel} audio play aborted (expected)`,
                    level: 'info',
                    data: {
                      browser: browserLabel,
                      error: error.name,
                      message: error.message,
                      action: 'play_aborted',
                      autoRetry: true,
                    },
                  });
                }
                // Keep newIsPlaying as true since it will auto-play when ready
              } else {
                // Log other Safari audio errors as potential issues
                handlePlaybackError(error, {
                  isSafari: requiresDelayedCreation,
                  browser: getBrowserLabel(),
                });
              }
            });
          } catch (error) {
            handlePlaybackError(error as Error, {
              isSafari: needsDelayedAudioCreation(),
              browser: getBrowserLabel(),
            });
          }
        } else {
          // Desktop behavior remains unchanged
          void audioElement.play().catch(handlePlaybackError);
          newIsPlaying = true;
        }
        break;
      case 'pause':
        if (audioElement) {
          audioElement.pause();
          newIsPlaying = false;
          log('Audio paused');

          // Log Safari pause operations for monitoring
          const requiresDelayedCreation = needsDelayedAudioCreation();
          if (requiresDelayedCreation) {
            const browserLabel = getBrowserLabel();
            Sentry.addBreadcrumb({
              category: 'safari_audio',
              message: `${browserLabel} audio paused successfully`,
              level: 'info',
              data: {
                action: 'pause_success',
                browser: browserLabel,
                audioPaused: audioElement.paused,
                currentTime: audioElement.currentTime,
                timestamp: new Date().toISOString(),
              },
            });
          }
        }
        break;
      case 'toggle':
        if (playback.isPlaying) {
          if (audioElement) {
            audioElement.pause();
            newIsPlaying = false;
            log('Audio paused via toggle');
          }
        } else {
          // Same mobile-specific handling for toggle
          if (isMobile) {
            try {
              const playPromise = audioElement.play();
              newIsPlaying = true;
              playPromise.catch(handlePlaybackError);
            } catch (error) {
              handlePlaybackError(error as Error);
            }
          } else {
            void audioElement.play().catch(handlePlaybackError);
            newIsPlaying = true;
          }
        }

        break;
      default:
        // No changes if an unknown action is provided
        break;
    }

    set(playbackAtom, { ...playback, isPlaying: newIsPlaying });
  }
);
playbackControlAtom.debugLabel = 'playbackControlAtom';
